import {type BoundingBoxSliderControl} from '@wandb/weave/common/components/MediaCard';
import {
  type BoundingBox2D,
  type LayoutType,
} from '@wandb/weave/common/types/media';
import {
  constString,
  type File,
  isAssignableTo,
  type Node,
  opArtifactVersionFile,
  opAssetArtifactVersion,
  opAssetFile,
  opGetRunTag,
  opRunConfig,
  replaceInputVariables,
  taggedValue,
  typedDict,
  type VoidNode,
  type WBImage,
} from '@wandb/weave/core';
import * as _ from 'lodash';
import React, {type FC, useCallback, useEffect, useMemo, useState} from 'react';

import {useWeaveContext} from '../../context';
import * as CGReact from '../../react';
import {ControlsImageOverlays} from './ControlImageOverlays';
import * as Controls from './controlsImage';
import {ImageOverlayLayout} from './ImageOverlayLayout';
import {DEFAULT_TILE_LAYOUT} from './ImageWithOverlays';
import type * as Panel2 from './panel';
import {useSignedUrlWithExpiration} from './useAssetFromArtifact';

const inputType = {type: 'image-file' as const};

interface PanelImageConfigType {
  hideImage?: boolean;
  tileLayout?: LayoutType;
  boxSliders?: {[valueName: string]: BoundingBoxSliderControl};
  overlayControls?: Controls.OverlayControls;
}
type PanelImageProps = Panel2.PanelProps<
  typeof inputType,
  PanelImageConfigType
>;

const LegacyQueryEngineMaskUrlResolver: FC<{
  index: number;
  mask: {loadedFrom: Node; path: string};
  onResolve: (index: number, directUrl: string) => void;
}> = ({index, mask, onResolve}) => {
  const fileNode = useMemo(
    () =>
      opArtifactVersionFile({
        artifactVersion: mask.loadedFrom as any,
        path: constString(mask.path),
      }) as
        | Node<{
            type: 'file';
            extension: string;
          }>
        | VoidNode,
    [mask.loadedFrom, mask.path]
  );
  const {signedUrl} = useSignedUrlWithExpiration(fileNode, 60 * 1000);

  useEffect(() => {
    if (signedUrl != null) {
      onResolve(index, signedUrl);
    }
  }, [index, onResolve, signedUrl]);

  return null;
};

const useClassLabels = (input: Node<typeof inputType>) => {
  const hasRunTag = isAssignableTo(
    input.type,
    taggedValue(typedDict({run: 'run'}), 'any')
  );

  const {loading: runConfigLoading} = CGReact.useNodeValue(
    opRunConfig({run: opGetRunTag({obj: input})}),
    {skip: !hasRunTag}
  );

  const {loading: runTagLoading, result: runTag} = CGReact.useNodeValue(
    opGetRunTag({obj: input}),
    {skip: !hasRunTag}
  );

  return useMemo(() => {
    if (
      !(runConfigLoading || runTagLoading) &&
      runTag != null &&
      runTag.configSubset != null
    ) {
      try {
        const configSubset: {[key: string]: any} = JSON.parse(
          runTag.configSubset
        );
        return _.get(configSubset, '_wandb.value.mask/class_labels', {});
      } catch (error) {
        console.error('Failed to parse config subset:', error);
        return {};
      }
    }
    return {};
  }, [runTag, runTagLoading, runConfigLoading]);
};

const PanelImageConfig: FC<PanelImageProps> = ({
  config,
  updateConfig,
  input,
}) => {
  const weave = useWeaveContext();

  const exemplarsNode = useMemo(() => {
    return replaceInputVariables(input, weave.client.opStore);
  }, [input, weave.client.opStore]);

  const classLabels = useClassLabels(exemplarsNode as Node<typeof inputType>);

  const {classSets, controls} = Controls.useImageControls(
    input.type,
    config?.overlayControls,
    classLabels
  );
  const updatedConfig = useMemo(() => {
    if (controls === config?.overlayControls) {
      return config;
    } else {
      return {
        ...config,
        overlayControls: controls,
      };
    }
  }, [config, controls]);

  const exemplars = CGReact.useNodeValue(exemplarsNode).result;
  const boxes = useMemo(() => {
    if (exemplars == null) {
      return {};
    }

    const exArray = Array.isArray(exemplars) ? exemplars : [exemplars];

    return Object.fromEntries(
      exArray.reduce(
        (
          memo: Array<[string, BoundingBox2D[]]>,
          example: WBImage,
          idx: number
        ) => {
          const {boxes: exampleBoxes} = example ?? {};
          if (exampleBoxes == null) {
            return memo;
          }
          return memo.concat(
            Object.entries(exampleBoxes).map(([key, box]) => {
              return [`${idx}-${key}`, box];
            })
          );
        },
        []
      )
    );
  }, [exemplars]);

  return (
    <ControlsImageOverlays
      boxes={boxes}
      controls={updatedConfig}
      classSets={classSets}
      updateControls={updateConfig}
    />
  );
};

const PanelImage: FC<PanelImageProps> = ({config, input}) => {
  const inputNode = input;
  const nodeValueQuery = CGReact.useNodeValue(inputNode);
  const assetFileNode = useMemo(
    () => opAssetFile({asset: inputNode}),
    [inputNode]
  ) as Node<File>;
  const {signedUrl: imageUrl} = useSignedUrlWithExpiration(
    assetFileNode,
    60 * 1000
  );
  const imageArtifact = useMemo(
    () => opAssetArtifactVersion({asset: inputNode}),
    [inputNode]
  );

  const image: WBImage = nodeValueQuery.result;

  const classLabels = useClassLabels(inputNode);

  const {
    maskControls: mergedMaskControls,
    boxControls: mergedBoxControls,
    classSets,
  } = Controls.useImageControls(
    inputNode.type,
    config?.overlayControls,
    classLabels
  );

  const {imageBoxes, imageMasks, boxControls, maskControls} = useMemo(() => {
    const knownBoxKeys = image?.boxes != null ? _.keys(image.boxes) : [];
    const knownMaskKeys = image?.masks != null ? _.keys(image.masks) : [];
    const mergedMaskControlsValues = Object.values(mergedMaskControls);
    const mergedBoxControlsValues = Object.values(mergedBoxControls);
    const controlBoxKeys = mergedBoxControlsValues.map(c => c.name.slice(4));
    const controlMaskKeys = mergedMaskControlsValues.map(c => c.name.slice(5));

    const validBoxKeys = _.intersection(knownBoxKeys, controlBoxKeys);
    const validMaskKeys = _.intersection(knownMaskKeys, controlMaskKeys);

    const imageBoxesRes =
      image?.boxes != null ? validBoxKeys.map(k => image.boxes![k]) : undefined;
    const imageMasksRes =
      image?.masks != null
        ? validMaskKeys.map(k => {
            return {
              layerName: k,
              loadedFrom: imageArtifact,
              path: image.masks![k]?.path,
            };
          })
        : undefined;

    const boxControlsRes = mergedBoxControlsValues.filter(c =>
      validBoxKeys.includes(c.name.slice(4))
    );
    const maskControlsRes = mergedMaskControlsValues.filter(c =>
      validMaskKeys.includes(c.name.slice(5))
    );
    return {
      imageBoxes: imageBoxesRes,
      imageMasks: imageMasksRes,
      boxControls: boxControlsRes,
      maskControls: maskControlsRes,
    };
  }, [image, mergedBoxControls, mergedMaskControls, imageArtifact]);

  const [maskUrls, setMaskUrls] = useState<Array<string | undefined>>([]);
  const handleMaskUrl = useCallback((index: number, directUrl: string) => {
    setMaskUrls(previous => {
      const next = [...previous];
      next[index] = directUrl;
      return next;
    });
  }, []);
  const resolvedImageMasks = useMemo(
    () => imageMasks?.map((_, index) => maskUrls[index]),
    [imageMasks, maskUrls]
  );

  const imageInCard = {
    directUrl: imageUrl ?? undefined,
    path: image?.path,
    width: image?.width,
    height: image?.height,
    caption: image?.caption,
  };

  return (
    <>
      {imageMasks?.map((mask, index) => (
        <LegacyQueryEngineMaskUrlResolver
          key={mask.layerName}
          index={index}
          mask={mask}
          onResolve={handleMaskUrl}
        />
      ))}
      <ImageOverlayLayout
        tileLayout={config?.tileLayout}
        hideImage={config?.hideImage}
        image={imageInCard}
        boundingBoxes={imageBoxes}
        masks={resolvedImageMasks}
        classSets={classSets}
        boxSliders={config?.boxSliders}
        maskControls={maskControls}
        boxControls={boxControls}
      />
    </>
  );
};

export const Spec: Panel2.PanelSpec<PanelImageConfigType> = {
  id: 'image-file',
  icon: 'photo',
  displayName: 'Image',
  ConfigComponent: PanelImageConfig,
  Component: PanelImage,
  inputType,
  canFullscreen: true,
  defaultFixedSize: config => {
    const {tileLayout = DEFAULT_TILE_LAYOUT} = config ?? {};

    let multiplier = 1;
    if (tileLayout === 'MASKS_NEXT_TO_IMAGE') {
      multiplier = 2;
    }
    if (tileLayout === 'ALL_SPLIT') {
      const masks = Object.values(config?.overlayControls ?? []).filter(
        v => v.type === 'mask'
      );
      multiplier = masks.length + 1;
    }

    return {
      width: 200 * multiplier,
      height: (9 / 16) * 200,
    };
  },
};
